import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, AuthTokens, UserRole } from '@/types'
import { authTokens, apiGet, apiPost, tryRefreshAccessToken } from '@/lib/api'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isSuperAdmin: boolean
  isAdmin: boolean
  hasRole: (role: UserRole | UserRole[]) => boolean
  login: (tokens: AuthTokens, userData?: User) => Promise<void>
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

// Backend user response interface
interface BackendUser {
  id: string
  email: string
  name?: string
  company?: string
  is_premium: boolean
  email_verified?: boolean
  role?: UserRole
  pending_email?: string
  mfa_enabled?: boolean
  mfa_enabled_at?: string
}

// Transform backend user response to match frontend User interface
function transformUserData(backendUser: BackendUser): User {
  // Split the name field into first_name and last_name
  const nameParts = (backendUser.name || '').trim().split(' ')
  const first_name = nameParts[0] || ''
  const last_name = nameParts.slice(1).join(' ') || ''

  return {
    user_id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name,
    is_premium: Boolean(backendUser.is_premium),
    email_verified: Boolean(backendUser.email_verified),
    role: backendUser.role || 'user',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      first_name: first_name || undefined,
      last_name: last_name || undefined,
      company: backendUser.company || undefined,
    },
    pending_email: backendUser.pending_email,
    mfa_enabled: Boolean(backendUser.mfa_enabled),
    mfa_enabled_at: backendUser.mfa_enabled_at,
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && authTokens.isAuthenticated()
  const isSuperAdmin = user?.role === 'superadmin'
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  // Check if user has specific role(s)
  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!user) return false
    const roles = Array.isArray(role) ? role : [role]
    return roles.includes(user.role)
  }

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true)

      // On a hard reload / direct deep-link the in-memory access token is gone,
      // but the HttpOnly refresh cookie may still be valid. Try a silent
      // refresh before giving up — otherwise protected routes bounce to /login
      // even for an authenticated user (issue #291).
      if (!authTokens.get().access_token) {
        const refreshed = await tryRefreshAccessToken()
        if (!refreshed) {
          setIsLoading(false)
          return
        }
      }

      try {
        // Verify token and get user data
        const response = await apiGet<BackendUser>('/users/profile')
        if (response.success && response.data) {
          const transformedUser = transformUserData(response.data)
          setUser(transformedUser)
        } else {
          // Token is invalid, clear it
          authTokens.clear()
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        authTokens.clear()
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (tokens: AuthTokens, userData?: any) => {
    authTokens.set(tokens)

    if (userData) {
      // Transform the user data if it's in backend format
      const transformedUser = 'user_id' in userData ? userData : transformUserData(userData as BackendUser)
      setUser(transformedUser)
    } else {
      // If no user data provided (OAuth flow), fetch it from the backend
      try {
        const response = await apiGet<BackendUser>('/users/profile')
        if (response.success && response.data) {
          const transformedUser = transformUserData(response.data)
          setUser(transformedUser)
        } else {
          throw new Error('Failed to fetch user profile')
        }
      } catch (error) {
        console.error('Failed to fetch user after OAuth login:', error)
        authTokens.clear()
        throw error
      }
    }
  }

  const logout = async () => {
    // Try to invalidate tokens on the backend
    // refresh_token is sent automatically via HttpOnly cookie
    try {
      await apiPost('/auth/logout', {})
    } catch (error) {
      // Don't block logout on backend failure
      console.error('Backend logout failed:', error)
    }
    authTokens.clear()
    setUser(null)
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData })
    }
  }

  const refreshUser = async () => {
    if (!authTokens.isAuthenticated()) {
      return
    }

    try {
      const response = await apiGet<BackendUser>('/users/profile')
      if (response.success && response.data) {
        const transformedUser = transformUserData(response.data)
        setUser(transformedUser)
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isAdmin,
    hasRole,
    login,
    logout,
    updateUser,
    refreshUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
